/**
 * Thinking Loader Store
 * 
 * Manages the contextual thinking loader that shows what the AI is "doing"
 * while waiting for the first token to arrive.
 * 
 * Features:
 * - Instant pre-written thinking steps (no API calls)
 * - Large randomized pools (30-50 steps per activity)
 * - Automatic cycling through steps
 * - Immediate stop when first token arrives
 */

import { create } from 'zustand';
import { getThinkingSteps } from '../services/thinkingStepsService';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('ThinkingLoader', { enabled: false });

export interface ThinkingStep {
  text: string;
  duration?: number; // Optional: how long to show this step (ms)
}

export interface ActivityContext {
  toolName?: string;
  documentName?: string;
  operation?: string;
  imagePrompt?: string;
}

interface ThinkingLoaderState {
  // Core state
  isWaitingForFirstToken: boolean;
  currentActivity: string | null;
  activityContext: ActivityContext | null;
  
  // Pre-written thinking steps
  thinkingSteps: ThinkingStep[];
  currentStepIndex: number;
  isLoadingSteps: boolean; // Always false now (kept for compatibility)
  
  // Timing
  stepStartTime: number | null;
  cycleIntervalId: NodeJS.Timeout | null;
  stepDuration: number; // How long to show each step (default 2500ms)
  
  // Actions
  startWaiting: (activity: string, context?: ActivityContext) => void;
  stopWaiting: () => void;
  updateActivity: (activity: string, context?: ActivityContext) => void;
  setThinkingSteps: (steps: ThinkingStep[]) => void;
  nextStep: () => void;
  reset: () => void;
}

export const useThinkingLoaderStore = create<ThinkingLoaderState>((set, get) => ({
  // Initial state
  isWaitingForFirstToken: false,
  currentActivity: null,
  activityContext: null,
  thinkingSteps: [],
  currentStepIndex: 0,
  isLoadingSteps: false,
  stepStartTime: null,
  cycleIntervalId: null,
  stepDuration: 3500, // 3.5 seconds per step (after first step which is 2 seconds)
  
  /**
   * Start waiting for first token - get pre-written steps instantly
   */
  startWaiting: (activity: string, context?: ActivityContext) => {
    const { cycleIntervalId } = get();
    
    // Clear any existing interval
    if (cycleIntervalId) {
      clearInterval(cycleIntervalId);
    }
    
    log.log('[ThinkingLoader] Starting waiting state:', { activity, context });
    
    // Get thinking steps immediately (synchronous)
    const steps = getThinkingSteps(activity);
    log.log('[ThinkingLoader] Loaded thinking steps:', { count: steps.length });
    
    // Set waiting state immediately with steps
    const startTime = Date.now();
    log.log(`🎯 [TIMING] Setting isWaitingForFirstToken=true at ${startTime}`);
    
    set({
      isWaitingForFirstToken: true,
      currentActivity: activity,
      activityContext: context || null,
      currentStepIndex: 0,
      isLoadingSteps: false, // No loading needed - instant steps
      stepStartTime: startTime,
      thinkingSteps: steps
    });
    
    log.log(`🎯 [TIMING] Store state updated at ${Date.now()}, delay: ${Date.now() - startTime}ms`);
    
    // Start cycling through steps
    // First interval is 2 seconds for "Thinking..." message
    const intervalId = setInterval(() => {
      const { isWaitingForFirstToken } = get();
      if (isWaitingForFirstToken) {
        get().nextStep();
      } else {
        // Stop if no longer waiting
        clearInterval(intervalId);
      }
    }, 2000); // First step shows for 2 seconds
    
    set({ cycleIntervalId: intervalId });
    
    // ✅ WATCHDOG: Auto-stop after 30 seconds ONLY if truly stuck
    // Very conservative - only catches catastrophic failures where stream never arrives
    setTimeout(() => {
      const { isWaitingForFirstToken, stepStartTime } = get();
      if (!isWaitingForFirstToken) {
        return; // Already stopped, nothing to do
      }
      
      // Check if we've been waiting for a VERY long time (30s+)
      const waitingDuration = Date.now() - (stepStartTime || 0);
      if (waitingDuration < 30000) {
        log.log('[ThinkingLoader] ℹ️ Watchdog: Timer fired but not stuck yet');
        return; // Not stuck, just slow
      }
      
      // After 30s, check if anything is happening
      try {
        // Look for ANY sign of activity in DOM
        const hasActiveCards = document.querySelectorAll('.tool-use-card.lighting-effect').length > 0;
        const hasStreamingText = document.querySelectorAll('.text-card').length > 0;
        const chatMessages = document.querySelector('.chat-messages');
        const hasAnyActivity = hasActiveCards || (chatMessages && chatMessages.children.length > 0);
        
        if (!hasAnyActivity) {
          // No activity for 30+ seconds → catastrophic failure
          log.warn('[ThinkingLoader] ⚠️ Watchdog: No activity for 30s+, auto-stopping (likely backend failure)');
          get().stopWaiting();
        } else {
          log.log('[ThinkingLoader] ✓ Watchdog: Activity detected, keeping thinking on');
        }
      } catch (error) {
        // DOM check failed - don't stop
        log.log('[ThinkingLoader] ℹ️ Watchdog: Could not verify state, keeping on (conservative)');
      }
    }, 30000); // 30 seconds - very conservative, only catches catastrophic failures
  },
  
  /**
   * Stop waiting - first token has arrived
   */
  stopWaiting: () => {
    const { cycleIntervalId } = get();
    
    log.log('[ThinkingLoader] Stopping waiting state');
    
    if (cycleIntervalId) {
      clearInterval(cycleIntervalId);
    }
    
    set({
      isWaitingForFirstToken: false,
      cycleIntervalId: null,
      currentStepIndex: 0,
      stepStartTime: null
    });
  },
  
  /**
   * Update activity while keeping thinking loader active
   * Used during agentic workflows to smoothly transition between activities
   */
  updateActivity: (activity: string, context?: ActivityContext) => {
    const { isWaitingForFirstToken, currentActivity } = get();
    
    // Only update if we're currently waiting
    if (!isWaitingForFirstToken) {
      log.log('[ThinkingLoader] ⚠️ Cannot update activity - not currently waiting');
      return;
    }
    
    log.log('[ThinkingLoader] Updating activity (smooth transition):', {
      from: currentActivity,
      to: activity
    });
    
    // Get new thinking steps for the activity
    const steps = getThinkingSteps(activity);
    log.log('[ThinkingLoader] Loaded new thinking steps:', { count: steps.length });
    
    // Check again right before setting state (prevent race condition)
    // If stopWaiting() was called while we were getting steps, don't update
    const stateBeforeSet = get();
    if (!stateBeforeSet.isWaitingForFirstToken) {
      log.log('[ThinkingLoader] ⚠️ Loader was stopped during update, aborting');
      return;
    }
    
    set({
      currentActivity: activity,
      activityContext: context || null,
      thinkingSteps: steps,
      currentStepIndex: 0,
      stepStartTime: Date.now()
      // Keep isWaitingForFirstToken = true (don't stop!)
      // Keep cycleIntervalId running
    });
  },
  
  /**
   * Set thinking steps (called when Haiku responds)
   */
  setThinkingSteps: (steps: ThinkingStep[]) => {
    const stepsArray = steps.map(step => 
      typeof step === 'string' ? { text: step } : step
    );
    
    set({
      thinkingSteps: stepsArray,
      isLoadingSteps: false,
      currentStepIndex: 0,
      stepStartTime: Date.now()
    });
    
    log.log('[ThinkingLoader] Thinking steps set:', stepsArray);
  },
  
  /**
   * Move to next step (called by interval)
   */
  nextStep: () => {
    const { thinkingSteps, currentStepIndex, cycleIntervalId } = get();
    
    if (thinkingSteps.length === 0) {
      // No steps yet, keep showing generic "Thinking..."
      return;
    }
    
    // Cycle through steps (loop back to 0)
    const nextIndex = (currentStepIndex + 1) % thinkingSteps.length;
    
    // When moving from step 0 to step 1, restart interval with 3.5 seconds
    if (currentStepIndex === 0 && nextIndex === 1) {
      // Clear the 2-second interval
      if (cycleIntervalId) {
        clearInterval(cycleIntervalId);
      }
      
      // Start new 3.5-second interval for remaining steps
      const newIntervalId = setInterval(() => {
        const { isWaitingForFirstToken } = get();
        if (isWaitingForFirstToken) {
          get().nextStep();
        } else {
          // Stop if no longer waiting
          clearInterval(newIntervalId);
        }
      }, get().stepDuration); // 3.5 seconds for subsequent steps
      
      set({ cycleIntervalId: newIntervalId });
    }
    
    set({
      currentStepIndex: nextIndex,
      stepStartTime: Date.now()
    });
  },
  
  /**
   * Reset all state
   */
  reset: () => {
    const { cycleIntervalId } = get();
    
    if (cycleIntervalId) {
      clearInterval(cycleIntervalId);
    }
    
    set({
      isWaitingForFirstToken: false,
      currentActivity: null,
      activityContext: null,
      thinkingSteps: [],
      currentStepIndex: 0,
      isLoadingSteps: false,
      stepStartTime: null,
      cycleIntervalId: null
    });
    
    log.log('[ThinkingLoader] State reset');
  }
}));

// Export selector hooks for convenience
export const useIsWaitingForFirstToken = () => 
  useThinkingLoaderStore(state => state.isWaitingForFirstToken);

export const useCurrentThinkingStep = () => 
  useThinkingLoaderStore(state => {
    const { thinkingSteps, currentStepIndex } = state;
    return thinkingSteps[currentStepIndex]?.text || null;
  });

export const useIsLoadingSteps = () => 
  useThinkingLoaderStore(state => state.isLoadingSteps);

